<template>
  <div class="c-icons">
    <div class="c-page-header c-page-header--flush">
      <div>
        <h3 class="c-page-header__title">
          Icons
        </h3>
        <p class="c-page-header__subtitle">
          Map icon names to the slugs they are shown for.
        </p>
      </div>

      <div
        v-if="canWrite(permissionName)"
        class="c-page-header__actions"
      >
        <button
          type="button"
          class="ui-btn ui-btn--primary"
          @click="createNewIcon()"
        >
          <app-icon name="add" />
          Add icon
        </button>
      </div>
    </div>

    <div class="c-page-toolbar c-page-toolbar--flush">
      <div class="ui-field c-page-toolbar__search">
        <div class="ui-field__control ui-field__control--with-action">
          <app-icon
            class="ui-field__icon"
            name="search"
          />
          <input
            v-model="searchQuery"
            class="ui-field__input"
            type="text"
            placeholder="Search by name or slug…"
            aria-label="Search icons"
            @input="handleSearch"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="ui-field__action"
            title="Clear search"
            aria-label="Clear search"
            @click="searchQuery = ''; handleSearch()"
          >
            <app-icon name="close" />
          </button>
        </div>
      </div>

      <span
        v-if="fetchedResults"
        class="c-page-toolbar__meta"
      >
        {{ totalLabel }}
      </span>
    </div>

    <div class="c-table-wrapper c-table-card">
      <base-table-component
        :prop-data="iconsForTableData"
        :empty-title="searchQuery ? 'No icons found' : 'No icons yet'"
        :empty-description="searchQuery ? `Nothing matches “${searchQuery}”.` : 'Add an icon to get started.'"
      >
        <template v-slot:default="slotProps">
          <span class="c-table__btn-wrap">
            <generic-button
              icon
              transparent
              text-inherit
              :disabled="!canWrite(permissionName)"
              @click="editIcon(slotProps.selected)"
            >
              <md-tooltip md-direction="top">
                Edit icon
              </md-tooltip>
              <img src="../../../../../assets/edit-button.svg" alt="">
            </generic-button>
          </span>
          <span class="c-table__btn-wrap">
            <generic-button
              icon
              transparent
              text-inherit
              :disabled="!canWrite(permissionName)"
              @click="openDeleteModal(slotProps.selected)"
            >
              <md-tooltip md-direction="top">
                Delete icon
              </md-tooltip>
              <img src="../../../../../assets/delete-button.svg" alt="">
            </generic-button>
          </span>
        </template>
      </base-table-component>
    </div>

    <paginate
      v-if="totalPages > 1"
      v-model="currentPage"
      :page-count="totalPages"
      :prev-text="'‹'"
      :next-text="'›'"
      :page-range="5"
      :margin-pages="1"
      :container-class="'c-pagination'"
      :prev-link-class="'md-button prev md-elevation-1'"
      :next-link-class="'md-button next md-elevation-1'"
      :page-link-class="'md-button md-elevation-1'"
      :break-view-link-class="'md-button'"
      :active-class="'active'"
      :disabled-class="'disabled'"
    />
  </div>
</template>

<script>
  import BaseTableComponent from '@/shared/table/BaseTableComponent';
  import GenericButton from '@/shared/buttons/GenericButton';
  import AppIcon from '@/shared/app-icon/AppIcon';
  import EditIconsModal from './edit';
  import DeleteModalComponent from '@/shared/delete-modal/DeleteModalComponent.vue';
  import { iconsService } from '@/services/icons';
  import filter from 'lodash/filter';
  import each from 'lodash/each';
  import { mapActions } from 'vuex';
  import { responseService } from '@/services/response';
  import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
  import {PERMISSION_NAMES} from "@/const/online-permissions";

  export default {
    name: 'OnlineIconsList',

    components: {
      BaseTableComponent,
      GenericButton,
      AppIcon,
    },

    mixins: [onlinePermissionsMixin],

    data() {
      return {
        permissionName: `${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.ICONS.key}`,

        searchQuery: '',

        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,

        fetchedResults: false,
        icons: [],
        listOfIconNames: [],
        listOfSlugNames: [],
      }
    },

    computed: {
      PERMISSION_NAMES() {
        return PERMISSION_NAMES
      },

      totalLabel() {
        const total = this.totalItems;

        return `${total} ${total === 1 ? 'icon' : 'icons'}`;
      },
      iconsForTableData() {
        if(!this.icons) {
          return [];
        }
        const currentPage = this.currentPage || 1;
        const unfilteredIcons = this.icons.map((icon, index) => ({
          iconIndex: index,
          icon: icon,
          rowData: {
            'Name': icon.name,
            'Slugs': icon.slugNames?.join(', '),
          }
        }))

        return this.filterIcons(unfilteredIcons)
          .slice((currentPage - 1) * this.itemsPerPage, currentPage * this.itemsPerPage);
      }
    },

    async created() {
      await this.getAllIcons(true);
    },

    methods: {
      ...mapActions("response", ["handleSuccess", "setGlobalError", "resetError"]),

      async handleCreatedIcon() {
        await this.getAllIcons();
        this.currentPage = 1;
      },

      createNewIcon() {
        this.$modal.show(
          EditIconsModal,
          {
            listOfIconNames: this.listOfIconNames,
            listOfSlugNames: this.listOfSlugNames,
            handleSuccessCallback: this.handleCreatedIcon,
          },
          {
            height: "auto", scrollable: false, width: "440px"
          }
        );
      },

      filterIcons(unfilteredIcons) {
        return filter(unfilteredIcons, icon => {
          if(icon.rowData) {
            return icon.icon.name.toLowerCase().includes(this.searchQuery.toLowerCase())
              || icon.icon.slugNames?.join(',').toLowerCase().includes(this.searchQuery.toLowerCase())
          }

          return icon.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            || icon.slugNames?.join(',').toLowerCase().includes(this.searchQuery.toLowerCase())
        })
      },

      handleSearch() {
        const filteredIcons = this.filterIcons(this.icons);
        this.handlePaginationParameters(filteredIcons.length);
      },

      editIcon(icon) {
        this.$modal.show(
          EditIconsModal,
          {
            fetchedIcon: {
              name: icon.rowData.Name,
              slugNames: icon.rowData.Slugs ? icon.rowData.Slugs.split(', ') : [''],
              id: icon.icon.id
            },
            icon: icon,
            listOfIconNames: this.listOfIconNames,
            listOfSlugNames: this.listOfSlugNames,
            handleSuccessCallback: this.getAllIcons,
          },
          {
            height: "auto", scrollable: false, width: "440px"
          }
        )
      },

      async deleteIcon(icon) {
        try {
          await iconsService.delete(icon.icon.id)
          this.handleSuccess({ message: "Successfully deleted icon" })
        } catch (error) {
          this.setGlobalError(responseService.getErrorMessage(error.data?.message));
        }
      },

      openDeleteModal(icon) {
        this.$modal.show(
          DeleteModalComponent,
          {
            deleteMsg: icon.icon.name,
            deleteMsgAlert: 'It will be deleted on all websites.',
            propData: icon,
            deleteFunction: this.deleteIcon,
            getDataFunction: this.getAllIcons,
            pageData: {}
          },
          { height: "auto", scrollable: false, width: "440px" }
        )
      },

      async getAllIcons(isInit = false) {
        try {
          const icons = await iconsService.getAllIcons();
          this.icons = icons.data.map(icon => {
            return {
              ...icon,
              slugNames: JSON.parse(icon.slugNames)
            }
          });

          this.listOfIconNames = [];
          this.listOfSlugNames = [];

          each(this.icons, (icon) => {
            this.listOfIconNames.push(icon.name);
            icon.slugNames ? this.listOfSlugNames.push(...icon.slugNames) : ''
          })

          this.handlePaginationParameters();
          this.fetchedResults = true;

          if(!isInit || this.icons.length) {
            return;
          }

        } catch (error) {
          this.setGlobalError(responseService.getErrorMessage(error.data.message));
        }
      },

      handlePaginationParameters(totalIconsCount = null) {
        const iconsCount = totalIconsCount !== null ? totalIconsCount : this.icons.length;

        this.totalPages = Math.ceil(iconsCount / this.itemsPerPage);
        this.totalItems = iconsCount;

        if(this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
        }
      }
    },
  }
</script>
